import { useState, useRef } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import {
  Upload, FileText, Loader2, CheckCircle2, ArrowLeft, X, Sparkles
} from 'lucide-react';

const FOLDER_NAMES = {
  A: 'O&M Data', B: 'Warranties', C: 'As-Builts', D: 'Attic Stock',
  E: 'Training', F: 'Cx / TAB', G: 'Service Contracts', H: 'Submittals', I: 'Other',
};

export default function DocumentUploadPanel({ projectId, divisions, onUploaded, onClose }) {
  const [file, setFile] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const [filing, setFiling] = useState(false);
  const [preview, setPreview] = useState(null);     // AI suggestion (before filing)
  const [result, setResult] = useState(null);       // filed document
  const [division, setDivision] = useState('');      // user-confirmed division id
  const [folder, setFolder] = useState('');          // user-confirmed folder letter
  const fileRef = useRef(null);

  // Step 1 — classify only (no storage, no record)
  const handleClassify = async () => {
    if (!file) return;
    setClassifying(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('preview', 'true');
      const res = await api.post(`/closeout/projects/${projectId}/upload-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setDivision(res.data?.resolved?.division_id || '');
      setFolder(res.data?.resolved?.folder_letter || '');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not classify document. Please try again.');
    }
    finally { setClassifying(false); }
  };

  // Step 2 — confirm & file with the (possibly changed) division/folder
  const handleConfirmFile = async () => {
    if (!file) return;
    setFiling(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (division) fd.append('division_id', division);
      if (folder) fd.append('folder_letter', folder);
      // Carry the AI's extracted title/spec + provenance through to the filed record
      if (resolved?.title) fd.append('title', resolved.title);
      if (ai?.spec_number) fd.append('spec_number', ai.spec_number);
      if (ai?.division_id) fd.append('ai_suggested_division', ai.division_id);
      if (ai?.folder_letter) fd.append('ai_suggested_folder', ai.folder_letter);
      if (ai?.confidence != null) fd.append('ai_confidence', String(ai.confidence));
      if (ai?.reasoning) fd.append('ai_reasoning', ai.reasoning);
      const res = await api.post(`/closeout/projects/${projectId}/upload-document`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      toast.success('Document filed');
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Could not file document. Please try again.');
    }
    finally { setFiling(false); }
  };

  const ai = preview?.ai_classification;
  const resolved = preview?.resolved;
  const aiChanged = preview && (
    division !== (resolved?.division_id || '') || folder !== (resolved?.folder_letter || '')
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="document-upload-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-sm w-[600px] max-h-[85vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-5 border-b border-border flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground">Upload Closeout Document</h2>
            <p className="text-xs text-muted-foreground mt-0.5">AI suggests where to file — you confirm before it's saved</p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground/80"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* ───── Step 3: Filed ───── */}
          {result ? (
            <div className="space-y-4">
              <div className="text-center py-4">
                <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-lg font-heading text-foreground">Document Filed</p>
              </div>
              {result.document?.composed_filename && (
                <div className="bg-card border border-border rounded-sm p-4">
                  <p className="text-[10px] font-heading uppercase tracking-widest text-emerald-500/80 mb-2">Filed As</p>
                  <p className="text-sm text-foreground/80 font-mono">{result.document.composed_filename}</p>
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button onClick={() => { setFile(null); setPreview(null); setResult(null); setDivision(''); setFolder(''); }}
                  className="px-4 py-2 text-xs font-heading uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                  Upload Another
                </button>
                <button onClick={() => { onUploaded(); onClose(); }} data-testid="upload-done-btn"
                  className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                  Done
                </button>
              </div>
            </div>

          /* ───── Step 2: Review AI suggestion & confirm ───── */
          ) : preview ? (
            <div className="space-y-5" data-testid="classify-preview">
              <div className="flex items-center gap-2 text-sm text-foreground/80">
                <FileText className="w-4 h-4 text-primary" />
                <span className="truncate">{file?.name}</span>
              </div>

              <div className="bg-violet-50 dark:bg-violet-950/20 border border-violet-200 dark:border-violet-800/40 rounded-sm p-4">
                <p className="flex items-center gap-1.5 text-[10px] font-heading uppercase tracking-widest text-violet-700 dark:text-violet-300 mb-2">
                  <Sparkles className="w-3 h-3" /> AI Suggestion
                </p>
                <div className="grid grid-cols-2 gap-3 text-xs">
                  <div><span className="text-muted-foreground">Division:</span>
                    <span className="ml-2 text-foreground/80 font-mono">{resolved?.csi_number} — {resolved?.trade_name}</span></div>
                  <div><span className="text-muted-foreground">Folder:</span>
                    <span className="ml-2 text-foreground/80 font-mono">{resolved?.folder_letter} — {FOLDER_NAMES[resolved?.folder_letter]}</span></div>
                  <div><span className="text-muted-foreground">Title:</span>
                    <span className="ml-2 text-foreground/80">{resolved?.title}</span></div>
                  {ai?.confidence != null && (
                    <div><span className="text-muted-foreground">Confidence:</span>
                      <span className={`ml-2 font-mono ${ai.confidence >= 0.8 ? 'text-emerald-500' : 'text-amber-500'}`}>{(ai.confidence * 100).toFixed(0)}%</span></div>
                  )}
                </div>
                {ai?.reasoning && <p className="text-[11px] text-muted-foreground mt-2 italic">{ai.reasoning}</p>}
              </div>

              {/* Confirm or change */}
              <div>
                <p className="text-[10px] font-heading uppercase tracking-widest text-muted-foreground mb-2">
                  Confirm or change where it's filed
                </p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] text-muted-foreground mb-1 block">Division</label>
                    <select value={division} onChange={(e) => setDivision(e.target.value)} data-testid="confirm-division-select"
                      className="w-full bg-card border border-border rounded-sm px-2 py-2 text-xs text-foreground/80 font-mono focus:outline-none focus:border-primary/50">
                      {divisions.map(d => (<option key={d.id} value={d.id}>{d.csi_number} — {d.trade_name}</option>))}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] text-muted-foreground mb-1 block">Folder</label>
                    <select value={folder} onChange={(e) => setFolder(e.target.value)} data-testid="confirm-folder-select"
                      className="w-full bg-card border border-border rounded-sm px-2 py-2 text-xs text-foreground/80 font-mono focus:outline-none focus:border-primary/50">
                      {Object.entries(FOLDER_NAMES).map(([l, n]) => (<option key={l} value={l}>{l} — {n}</option>))}
                    </select>
                  </div>
                </div>
                {aiChanged && <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1.5">You've changed the AI's suggestion.</p>}
              </div>

              <div className="flex justify-between gap-3 pt-1">
                <button onClick={() => { setPreview(null); }} data-testid="classify-back-btn"
                  className="flex items-center gap-1.5 px-4 py-2 text-xs font-heading uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors">
                  <ArrowLeft className="w-3.5 h-3.5" /> Back
                </button>
                <button onClick={handleConfirmFile} disabled={filing} data-testid="confirm-file-btn"
                  className="flex items-center gap-2 px-5 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                  {filing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                  {filing ? 'Filing...' : 'Confirm & File'}
                </button>
              </div>
            </div>

          /* ───── Step 1: Pick file ───── */
          ) : (
            <div className="space-y-5">
              <div
                onClick={() => fileRef.current?.click()}
                className={`flex flex-col items-center justify-center h-36 border-2 border-dashed rounded-sm cursor-pointer transition-colors ${
                  file ? 'border-primary/40 bg-orange-950/10' : 'border-border hover:border-slate-600'
                }`}
              >
                {file ? (
                  <>
                    <FileText className="w-8 h-8 text-primary mb-2" />
                    <p className="text-sm text-foreground/80">{file.name}</p>
                    <p className="text-xs text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
                  </>
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">Drop or click to select PDF, PNG, or JPEG</p>
                  </>
                )}
                <input ref={fileRef} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg"
                  onChange={(e) => setFile(e.target.files[0])} />
              </div>

              <div className="flex justify-end">
                <button onClick={handleClassify} disabled={!file || classifying} data-testid="classify-document-btn"
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-xs font-heading uppercase tracking-wider rounded-sm transition-colors">
                  {classifying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                  {classifying ? 'Classifying...' : 'Classify Document'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
